'use client';

import {
  convictionFraction,
  convictionPercent,
  useAddToWatchlist,
  useGetMarketsField,
  useRemoveFromWatchlist,
} from '@morfi/backend-sdk-v2/client';
import { isMarketResolved } from '@morfi/contract-sdk';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type {
  ForwardedRef,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  RefObject,
} from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import type { LiveEventSummary } from '@/components/live/types';
import {
  IdentityBeam,
  MarketFeedIdentityBeam,
  MarketFeedOriginMark,
  OriginMark,
} from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import {
  FeedMarketThresholdProvider,
  useFeedMarketThreshold,
} from '@/components/markets/feed-market-threshold';
import { LiveTradesOverlay } from '@/components/markets/live-trades/live-trades-overlay';
import { useMarketFeedTrades } from '@/components/markets/live-trades/market-feed-trade-provider';
import type { LiveTrade } from '@/components/markets/live-trades/types';
import { MarketOutcomeHover } from '@/components/markets/market-outcome-hover';
import {
  outcomeLabelTooltipArrowClassName,
  outcomeLabelTooltipClassName,
  outcomeLabelTooltipContentStyle,
  useOutcomeLabelTruncation,
} from '@/components/markets/outcome-label-tooltip';
import {
  buildOutcomeRowSummaries,
  type OutcomeRowSummary,
} from '@/components/markets/outcome-row-summary';
import { OutcomeStack } from '@/components/markets/outcome-stack';
import { StackedMarketCardFooter } from '@/components/markets/stacked-market-card-footer';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Countdown } from '@/components/ui/countdown';
import { Icon } from '@/components/ui/icon';
import {
  ArrowDataTransferHorizontalIconSolid,
  Award01IconSolid,
  ChampionIconSolid,
  CheckmarkCircle02IconSolid,
  Plant04IconSolid,
  Target02IconSolid,
  Timer01Icon,
} from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { getLocationFlag } from '@/config/proposal-options';
import { TOAST_IDS } from '@/config/toast-ids';
import { MARKETS_BOOKMARK_TOGGLED, MARKETS_CARD_CLICKED } from '@/events/markets';
import {
  type OutcomeColorClasses,
  type OutcomeHueName,
  useOutcomeColor,
} from '@/hooks/chart/use-outcome-color';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useAutoScrollToLeader } from '@/hooks/use-auto-scroll-to-leader';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { trackEvent } from '@/lib/analytics';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { type WatchlistKind, watchlistKindForStatus } from '@/lib/market/watchlist-kind';
import type { PatternParams } from '@/lib/pattern-studio';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_HOVER, MOTION_POSITION } from '@/lib/ui/easing';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useMarketsSortStore } from '@/stores/markets-sort';

// `OutcomeRowSummary` is the canonical shape — re-exported from the shared
// outcome-summary module so existing importers (sparkline grid, tests) keep working.
export type { OutcomeRowSummary } from '@/components/markets/outcome-row-summary';

// ---------------------------------------------------------------------------
// Re-render isolation — read this before touching the component graph
// ---------------------------------------------------------------------------
//
// The `/markets` feed card is split into FOUR layers so a trade-driven field
// patch re-renders ONLY the region that displays that field — never the whole
// card. Measured: before the split a conviction tick re-rendered the entire
// `MarketRowCardView` (~3.5 renders·s⁻¹·card⁻¹ under a 50-dispatch·s⁻¹
// firehose), dragging the watchlist hooks, currency hooks, title, and cover
// image with it.
//
//   MarketRowCard (shell)   — reads ONLY stable fields (title, cover, category,
//                             location, outcomeCount). Never re-renders after
//                             first paint, so its children's element identity
//                             is frozen.
//   MarketRowCardChrome     — <Link>/<Panel>/title row. Receives `barGrid` +
//                             `footer` as ReactNode props. Because the shell
//                             doesn't re-render, those nodes keep identity and
//                             the chrome never re-renders on a trade.
//   FeedBarGrid             — reads `outcomes` (conviction) + the live threshold.
//                             A conviction tick re-renders THIS and nothing else.
//   FeedFooter              — reads `volumeTotal` / `prizePool`; its threshold
//                             segment reads the card's one decay clock itself.
//
// `CardBarGrid` / `CardFooter` / `MarketRowCardChrome` are explicit-props
// presentational components shared with `MarketRowCardView` — the surface used
// by callers with synthetic, not-yet-on-chain markets (launchpad creation
// preview, workspace QA showcase). Those callers pass static props, so the
// view re-rendering wholesale costs nothing.

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Presentational props for `MarketRowCardView` — the pure card body. The
 * `/markets` feed uses the SDK-leaf `MarketRowCard` shell instead; this
 * explicit-props view is for surfaces that have a synthetic / not-yet-on-chain
 * market (the launchpad creation preview, the workspace QA showcase).
 */
export interface MarketRowCardViewProps {
  outcomeLayout?: 'bars' | 'stacked';
  surface?: 'card' | 'list';
  onSelectOutcome?: (originalIndex: number) => void;
  onOpenMarket?: () => void;
  bookmark?: ReactNode;
  identity?: MarketIdentityData;
  /** Workspace previews may show identity chrome independently of rollout. */
  identityPreview?: boolean;
  pubkey: string;
  title: string;
  coverImageUrl: string;
  /**
   * NSFW flag for `coverImageUrl` (spec 071). When true the cover image
   * thumbnail is rendered through the NSFW-blur overlay until the viewer
   * taps to reveal. Sourced from the SDK `coverImageNsfw` field on the
   * market row. Defaults to `false` at the callsite when the parent has
   * a backend version that predates the field.
   */
  coverImageNsfw?: boolean;
  location: string | null;
  category: string | null;
  volumeTotal: string;
  /** Accumulated prize pool, D6-encoded USDC string. Rendered in the footer alongside the resolves-threshold and traded segments. */
  prizePool: string;
  outcomes: OutcomeRowSummary[];
  position: number;
  activeSort: string;
  /**
   * Live TWAR consensus threshold as a D9-encoded share string (0..1e9).
   * The bar fill scales so that a share equal to the threshold renders 100%
   * of container height — i.e. an outcome at threshold is at "100% of
   * consensus" visually. Threshold can move over time; bars re-tween
   * naturally as the prop updates. The percentage label inside each bar
   * still shows the outcome's actual share (not the scaled value).
   */
  consensusThreshold: string | bigint | null;
  /**
   * Caller-supplied live trades (spec 065) for the explicit-props
   * `MarketRowCardView` — the launchpad proposal preview (market does not
   * exist yet) and the workspace QA showcase (synthetic fixtures). Omitted →
   * an empty tape.
   *
   * The `/markets` feed shell (`MarketRowCard`) does not use this path: its
   * `FeedBarGrid` selects the market's buffered tape from the feed-level live
   * trade router and passes it down to every outcome's overlay.
   */
  liveTrades?: readonly LiveTrade[];
  /**
   * SDK-projected post-mount live trade ids — paired with `liveTrades` and
   * forwarded to every `LiveTradesOverlay`. Caller-owned preview / QA paths
   * pass an empty array (no NATS subscription, nothing fresh by definition).
   */
  liveSinceMountIds?: readonly string[];
  /**
   * Spec 071 — when true, NSFW-flagged outcome thumbnails are blurred but the
   * tap-to-reveal overlay is suppressed. Used by the proposal-creation preview
   * (the creator already knows what they uploaded — blur is a UX nudge, not a
   * gate). The cover image's reveal behaviour is unaffected. Default: false.
   */
  suppressOutcomeReveal?: boolean;

  /**
   * Workspace-only debug override — when set, forwards live halftone knob
   * state into cover and outcome `OptimizedImage` entities so they re-render
   * through `renderManifestToBlob` with the supplied params instead of
   * the lib's committed `DEFAULT_PARAMS`. Production callsites MUST omit this
   * prop. Used by /workspace/pattern.
   */
  imageOverride?: { params: PatternParams } | undefined;
  /**
   * Anchor `target` for the card link. Omit for in-app same-tab nav (the
   * default). Used by the similar-markets section on desktop to open
   * neighbour markets in a new tab without leaving the source page.
   */
  target?: string | undefined;
  /** Anchor `rel`. Pair with a `_blank` target as `"noopener noreferrer"`. */
  rel?: string | undefined;
  /**
   * Tailwind horizontal-padding class for outcome label pills. Default
   * `px-4` (16px). Showcase cards pass `px-2` for tighter three-outcome labels.
   */
  outcomeLabelPaddingClass?: string | undefined;
  /**
   * Disables the card, outcome-bar, and outcome-pill <Link>s plus every
   * hover-intent route/data prefetch. REQUIRED for synthetic pubkeys
   * (landing showcase, hero mockup, launchpad preview, workspace fixtures):
   * without it, hovering the card route-prefetches `/markets/<fake-id>`,
   * whose SSR then hits the backend with a non-base58 pubkey (the 2026-07-02
   * `landing-showcase-center` 4xx/500 noise). Default false (real markets).
   */
  linkDisabled?: boolean;
  /** Event-page presentation: replaces the consensus threshold with time until event resolution. */
  liveEvent?: MarketRowCardLiveEvent;
  /** Resolved preview presentation for explicit fixture cards. */
  resolved?: {
    winnerOriginalIndex: number;
    resolvedAt: string;
  };
}

export interface MarketRowCardLiveEvent {
  event: LiveEventSummary;
  initialNowMs: number;
}

// ---------------------------------------------------------------------------
// Module-scope constants
// ---------------------------------------------------------------------------

export const CONTAINER_HEIGHT_PX = 144;

// REASON: OptimizedImage's outcome entity requires a `hue` field; on the rare
// colour-resolution-fallback branch (`c === undefined` from useOutcomeColor)
// no hue is available. The fallback is only used when the upload URL fails,
// at which point a deterministic palette-tinted skeleton stands in for the
// image — `green` is a safe, neutral default that matches no specific outcome.
const FALLBACK_HUE: OutcomeHueName = 'green';

/**
 * Card aspect-ratio lock from md upward (4/3). `md:aspect-[4/3]` cascades
 * into lg and xl, so every desktop card is the same deterministic height as
 * its grid-cell width — the bar region (`flex-1`, `min-h-0` at md+) shrinks
 * to fit inside the locked box. This is what makes the skeleton→loaded swap
 * zero-CLS at every breakpoint: the skeleton shares the identical lock, so it
 * cannot drift on the card's content. Mobile (below md) stays content-driven
 * with the `min-h-36` bar floor.
 */
const ASPECT_RATIO_CLASS = 'md:aspect-[4/3]';

/**
 * Hover-revealed marketcap row below the percentage. The slot height animates
 * from 0 to MCAP_ROW_HEIGHT_PX so the percentage label sits flush at the bottom
 * of the bar-box at rest and lifts up only when the bar-box is hovered.
 * MCAP_ROW_HEIGHT_PX bakes in 4 px of top padding above the 14 px text line so
 * spacing from the percentage matches the resting layout's vertical rhythm
 * without a separate margin animation. The label additionally fades in and
 * slides up ~4 px so it appears to rise from the bottom edge as the slot opens.
 */
const MCAP_ROW_HEIGHT_PX = 18;
const MCAP_INITIAL = { opacity: 0, y: 4, height: 0 } as const;
const MCAP_ANIMATE = { opacity: 1, y: 0, height: MCAP_ROW_HEIGHT_PX } as const;
const MCAP_EXIT = { opacity: 0, y: 4, height: 0 } as const;

// `AnimatedOutcomePercent` (the outcome %) + its perf rationale now live in
// the shared `./animated-outcome-percent` module — also used by the hero.

interface MarketRowOutcomeLabelButtonProps {
  label: string;
  /** Original outcome index — used by `data-testid` only. */
  originalIndex: number;
  c: OutcomeColorClasses | undefined;
  ariaLabel: string;
  /** Outcome-scoped market deep-link (`/markets/{pubkey}?outcome=…`). */
  href: string;
  /** Fires analytics on activation; navigation itself is the `<Link>`'s job. */
  onSelect: () => void;
  /** Horizontal padding utility — default `px-4` (16px each side). */
  labelPaddingClass?: string | undefined;
  /** Resolved-market treatment: `'winner'` keeps the outcome palette with a
   *  trophy; `'loser'` dims it. Undefined for open markets (default styling). */
  resolvedState?: 'winner' | 'loser' | undefined;
}

/** Trophy + truncating label — shared by the pill's <Link> and non-nav <span> bodies. */
function OutcomePillContent(props: {
  label: string;
  resolvedState: 'winner' | 'loser' | undefined;
  setLabelSpanEl: (el: HTMLSpanElement | null) => void;
}): React.ReactNode {
  const { label, resolvedState, setLabelSpanEl } = props;
  return (
    <>
      {resolvedState === 'winner' ? (
        <Icon icon={Award01IconSolid} className="size-3 shrink-0" />
      ) : null}
      <span
        ref={setLabelSpanEl}
        className={cn(
          'min-w-0 truncate whitespace-nowrap',
          // Winner: content-sized so the trophy + label centre together as
          // one group (the button's `justify-center` does the centring).
          // Non-winner: fill + centre like every other outcome pill.
          resolvedState === 'winner' ? 'text-center' : 'flex-1 basis-0 text-center',
        )}
      >
        {label}
      </span>
    </>
  );
}

/** Outcome pill: tooltip with full label only when CSS truncation is active. */
function MarketRowOutcomeLabelButton(props: MarketRowOutcomeLabelButtonProps): React.ReactNode {
  const {
    label,
    originalIndex,
    c,
    ariaLabel,
    href,
    onSelect,
    resolvedState,
    labelPaddingClass = 'px-4',
  } = props;
  const { setLabelSpanEl, isTruncated } = useOutcomeLabelTruncation(label);
  // Full route prefetch on hover/focus/touch — the pill stays prefetch={false}
  // so nothing fires on viewport; the click then paints data-complete.
  // An empty href (synthetic pubkey, `linkDisabled`) renders a plain label
  // instead of a <Link> and never spreads the intent handlers, so hovering a
  // showcase/preview card cannot prefetch a route for a fake market id.
  const navigable = href !== '';
  const intent = useIntentRoutePrefetch(href);

  const buttonClasses = cn(
    // `relative z-10` keeps the pill above the card's stretched-Link overlay so
    // its outcome-scoped destination wins over the whole-card nav;
    // `pointer-events-auto` opts it back in under the click-through bar grid.
    // `gap-1` sits the winner's trophy directly left of the label (no effect
    // on the single-child non-winner pill).
    'pointer-events-auto relative z-10 w-full min-w-0 rounded-control border border-transparent font-medium text-body overflow-hidden gap-1',
    labelPaddingClass,
    (resolvedState === undefined || resolvedState === 'winner') &&
      (c === undefined
        ? 'bg-background-tertiary text-foreground-secondary hover:bg-background-tertiary'
        : cn(c.outline, c.hoverFill, c.text)),
    // Resolved markets: the winning outcome retains its outcome identity and
    // adds a trophy; the others dim back so the winner stands out.
    resolvedState === 'loser' && 'bg-background-tertiary text-foreground-tertiary opacity-60',
  );

  return (
    <AppTooltip
      content={label}
      disabled={!isTruncated}
      side="bottom"
      align="center"
      delayDuration={100}
      contentClassName={outcomeLabelTooltipClassName(c)}
      contentStyle={outcomeLabelTooltipContentStyle(c)}
      arrowClassName={outcomeLabelTooltipArrowClassName(c)}
    >
      {/* asChild → the outcome pill IS a real <Link> (no anchor-in-anchor now
          that the card chrome uses a stretched-Link overlay), so it soft-navs
          to the outcome-scoped market route. `prefetch={false}` + the hover
          `{...intent}` route prefetch = no viewport storm, instant on click. */}
      <AnimatedButton
        asChild
        variant="tertiary"
        size="sm"
        disableHoverScale
        disableTapScale
        aria-label={ariaLabel}
        className={buttonClasses}
      >
        {navigable ? (
          <Link
            href={href}
            prefetch={false}
            {...intent}
            onClick={onSelect}
            data-testid={`outcome-button-${String(originalIndex)}`}
          >
            <OutcomePillContent
              label={label}
              resolvedState={resolvedState}
              setLabelSpanEl={setLabelSpanEl}
            />
          </Link>
        ) : (
          <span data-testid={`outcome-button-${String(originalIndex)}`}>
            <OutcomePillContent
              label={label}
              resolvedState={resolvedState}
              setLabelSpanEl={setLabelSpanEl}
            />
          </span>
        )}
      </AnimatedButton>
    </AppTooltip>
  );
}

interface BarBoxImageLayersProps {
  /** Sorted index — used by `data-testid` only. */
  index: number;
  /** Outcome's index in the original (unsorted) outcomes array. */
  originalIndex: number;
  pubkey: string;
  /** Resolved outcome hue, or `undefined` on the colour-resolution fallback. */
  hue: OutcomeHueName | undefined;
  /** Decorative outcome image URL (spec 062). */
  imageUrl: string | null | undefined;
  /** NSFW flag for `imageUrl` (spec 071). */
  imageNsfw: boolean;
  /** True once the bar-box has been hovered at least once — gates the raster overlay mount. */
  hasHovered: boolean;
  /** True while the bar-box is currently hovered — drives the raster crossfade. */
  isHovered: boolean;
  suppressOutcomeReveal: boolean;
  imageOverride: { params: PatternParams } | undefined;
}

/**
 * Re-render-isolated image layer stack for one bar-box — the outcome-hue
 * overlay, the base `<OptimizedImage>`, and the hover raster `<OptimizedImage>`.
 *
 * This subtree is the expensive, share-INDEPENDENT part of a bar: image decode
 * + Pattern Studio render + IntersectionObserver. Its props are all primitives
 * (`pubkey`, `originalIndex`, `hue`, `imageUrl`, …) plus the mouse-driven
 * `isHovered` / `hasHovered` flags — none of which change on a trade. Because
 * the parent `MarketRowBarBox` re-renders on every conviction tick (the
 * percentage label + bar-fill height are genuinely share-dependent), keeping
 * these layers INLINE would re-run the image stack on every tick. Hoisting
 * them into a separate component lets the React Compiler memoize the call:
 * with referentially-stable props the compiler bails this subtree, so a trade
 * no longer re-decodes sibling outcomes' images.
 */
function BarBoxImageLayers(props: BarBoxImageLayersProps): React.ReactNode {
  const {
    index,
    originalIndex,
    pubkey,
    hue,
    imageUrl,
    imageNsfw,
    hasHovered,
    isHovered,
    suppressOutcomeReveal,
    imageOverride,
  } = props;
  return (
    <>
      {/*
        Overlay foreground (mirrors Figma node 3426:129) — a vivid
        outcome-hue wash (`--outcome-${hue}-400`) painted directly
        beneath the image. The bar-box wrapper's `c.outline` background
        isn't symmetric across themes: in dark mode it's a 5%-alpha
        vivid hue on a dark card (already reads as hue), but in light
        mode it's a fully-opaque pale-50 pastel that washes the
        surface toward white. Without this overlay, light-mode bars
        look washed out — image at 35% opacity blends toward the pale
        base and outcome identity disappears.
        Alpha is theme-aware via `--market-card-bar-overlay-foreground-alpha`
        (0.15 light / 0.10 dark) so the same vivid hue token reads
        correctly against both surfaces. Uses `--outcome-${hue}-400`
        directly (not the theme-shifting `-text` token) so the hue
        stays vivid in both modes.
      */}
      {hue !== undefined ? (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background: `color-mix(in srgb, var(--outcome-${hue}-400) calc(var(--market-card-bar-overlay-foreground-alpha) * 100%), transparent)`,
          }}
          data-testid={`bar-overlay-foreground-${String(index)}`}
        />
      ) : null}
      {/*
        Base layer — raw uploaded image dimmed to 35% so the outcome-hue
        backdrop bleeds through. The wrapper div carries the opacity —
        `imageClassName` gets `cn()`-merged with the load-state's
        `opacity-100` and tailwind-merge keeps the LAST opacity, dropping
        ours. A wrapper outside that class chain isn't subject to the
        dedupe. `absolute inset-0` makes the wrapper fill the bar-box;
        `fill` on Next's <Image> inside then anchors to the wrapper.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-35"
        data-testid={`bar-image-wrapper-${String(index)}`}
      >
        <OptimizedImage
          variant="hero"
          entity={{
            kind: 'outcome',
            marketPubkey: pubkey,
            outcomeIndex: originalIndex,
            hue: hue ?? FALLBACK_HUE,
            imageUrl: imageUrl ?? null,
            nsfw: imageNsfw,
          }}
          alt=""
          // ALLOW-NO-PREVIEW: rendered inside an interactive card (role="link" with onClick) — nested click targets would steal navigation
          preview={false}
          objectPosition="top"
          // Bar columns are at most ~200 CSS px wide (3-column desktop grid /
          // narrowest mobile bar). The hero variant's default sizes claim
          // `100vw` on mobile, which made Next pick a 640+ bucket and serve
          // the full-resolution upload for a 24–47 px display (2026-05-24
          // /demo-mode trace, ~290 kB wasted across 7 images). A flat pixel
          // value forces Next to pick from `imageSizes: [48, 96, 128, 256]`.
          sizes="200px"
          // Creator already knows what they uploaded — blur is a flag, not a gate. No reveal button.
          suppressReveal={suppressOutcomeReveal}
          {...(imageOverride !== undefined ? { imageOverride } : {})}
        />
      </div>
      {/*
        Hover layer — rasterised version of the same image (dithered
        through the outcome-hue palette by the Pattern Studio pipeline).
        Stacks on top of the base at the same position and fades in via
        opacity whenever the bar is hovered. Only mounts after the first
        hover (`hasHovered`) so we don't pay the network cost of every
        outcome's pattern PNG on initial render. Once mounted we keep it
        in the tree so re-hovers are a pure opacity transition.
      */}
      {hasHovered ? (
        <div
          aria-hidden="true"
          className={cn(
            'absolute inset-0 transition-opacity duration-normal ease-default motion-reduce:transition-none',
            // Halftone raster (outcome-hue palette via `forceRaster`). Peak
            // alpha is 50% so the hover layer doesn't overpower the 35%
            // opacity passthrough base.
            //
            // Light mode applies `contrast(0.55) brightness(1.3)` via
            // `--market-card-bar-hover-raster-filter` to lift dark dots on
            // the pale bar surface; dark mode uses `none`. Filter sits on
            // the overlay so it cascades to the raster PNG once it paints.
            'filter-(--market-card-bar-hover-raster-filter)',
            isHovered ? 'opacity-50' : 'opacity-0',
          )}
          data-testid={`bar-image-raster-${String(index)}`}
        >
          <OptimizedImage
            variant="hero"
            entity={{
              kind: 'outcome',
              marketPubkey: pubkey,
              outcomeIndex: originalIndex,
              hue: hue ?? FALLBACK_HUE,
              imageUrl: imageUrl ?? null,
              nsfw: imageNsfw,
            }}
            alt=""
            // ALLOW-NO-PREVIEW: rendered inside an interactive card (role="link" with onClick) — nested click targets would steal navigation
            preview={false}
            forceRaster
            // The wrapper around this image owns the dominant reveal —
            // it fades from opacity-0 to opacity-50 over
            // `--duration-normal` on hover. The hero variant's default
            // 600–700 ms scale/blur/opacity load-tween would compound on
            // top and make the FIRST hover feel slow (~700 ms total).
            // `'soft'` swaps in a light 300 ms opacity-only fade so the
            // rastered image dissolves in without re-introducing the
            // heavy scale/blur reveal.
            loadReveal="soft"
            objectPosition="top"
            // No `sizes` hint here (unlike the passthrough base layer above):
            // `forceRaster` resolves to the `/api/pattern/<hash>.png` route,
            // which `OptimizedImage` always renders `unoptimized`. Next emits
            // no `srcset` for an unoptimized image, so any `sizes` value is
            // inert — the source dimensions are fixed by the rasteriser route
            // (`?w=512&h=512`), not picked from a `sizes`-driven bucket.
            suppressReveal={suppressOutcomeReveal}
            {...(imageOverride !== undefined ? { imageOverride } : {})}
          />
        </div>
      ) : null}
    </>
  );
}

interface MarketRowBarBoxProps {
  outcome: OutcomeRowSummary;
  /** Sorted index — used by `data-testid` only. */
  index: number;
  /** Rounded percentage (0..100) for the headline label. */
  pct: number;
  /**
   * When true, the headline label renders with 2-decimal precision (the
   * fractional digits one type-step smaller). Set card-wide by
   * `CardBarGrid` when two outcomes round to the same whole percent.
   */
  decimals: boolean;
  /** CSS height for the animated bar fill, e.g. `"42%"`. */
  heightPercent: string;
  c: OutcomeColorClasses | undefined;
  imageVariantActive: boolean;
  pubkey: string;
  /**
   * Per-card live-trade tape — already mapped to `LiveTrade[]` by the parent
   * (`FeedBarGrid` for the feed, the caller for preview / QA). The feed-level
   * router supplies every outcome's overlay; the overlay itself is purely
   * presentational.
   */
  liveTrades: readonly LiveTrade[];
  /** SDK-projected post-mount live trade ids — see CardBarGrid props. */
  liveSinceMountIds: readonly string[];
  reduceMotion: boolean | null;
  /** Format an integer D6 USDC amount without crossing a floating-point boundary. */
  formatMcap: (valueD6: string) => string;
  /** Outcome-scoped market deep-link. Empty for synthetic, non-navigable cards. */
  href: string;
  /** Accessible name for the stretched outcome link. */
  ariaLabel: string;
  /** Fires the same analytics event as the matching outcome label link. */
  onSelect: () => void;
  /**
   * When true, the outcome image's NSFW reveal button is suppressed. Blur
   * still applies. See `MarketRowCardViewProps.suppressOutcomeReveal`.
   */
  suppressOutcomeReveal: boolean;
  /** See `MarketRowCardViewProps.imageOverride`. */
  imageOverride: { params: PatternParams } | undefined;
}

/**
 * Single bar column: outline + optional outcome image + animated fill +
 * percentage label + hover-revealed marketcap. Shared by both the scroll-mode
 * (>3 outcomes) and two-row (≤3 outcomes) layouts so hover state and the
 * mcap reveal animation live in one place.
 *
 * On hover, the effective marketcap (formatted as compact USDC) fades
 * in from below in a fixed 14 px slot reserved above the percentage. The slot
 * is empty by default — no layout shift on hover. Hover is desktop-only by
 * nature; touch users see the resting state.
 */
function MarketRowBarBox(props: MarketRowBarBoxProps): React.ReactNode {
  const {
    outcome,
    index,
    pct,
    decimals,
    heightPercent,
    c,
    imageVariantActive,
    pubkey,
    liveTrades,
    liveSinceMountIds,
    reduceMotion,
    formatMcap,
    href,
    ariaLabel,
    onSelect,
    suppressOutcomeReveal,
    imageOverride,
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  // Latches once the bar has been hovered at least once. The rasterised hover
  // overlay (the second `<OptimizedImage forceRaster>` below) mounts only
  // after that — eager mounting would double the network spend on the markets
  // list (every market card has up to 6 outcomes, each with its own raster
  // pattern PNG). Once mounted we keep the overlay in the tree so subsequent
  // hovers are an instant opacity crossfade instead of a remount + decode +
  // reveal animation.
  const [hasHovered, setHasHovered] = useState(false);
  // Two-phase mount: the wrapper starts at 0% height on first render, the
  // post-mount effect flips this to true and the CSS `transition: height`
  // smoothly grows the bar to its target. Subsequent share-drift updates use
  // the same transition. There is NO motion-driven entrance animation, so a
  // sort reorder cannot retrigger a "grow from the bottom" animation — the
  // height value transitions continuously between snapshots.
  const hasMounted = useHydrated();
  const effectiveHeight = hasMounted || reduceMotion === true ? heightPercent : '0%';
  const intent = useIntentRoutePrefetch(href);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: REASON: the parent div only owns the hover-driven market-cap reveal; the child stretched Link owns the outcome navigation and keyboard semantics.
    <div
      onMouseEnter={(): void => {
        setIsHovered(true);
        setHasHovered(true);
      }}
      onMouseLeave={(): void => setIsHovered(false)}
      className={cn(
        // `clip-path` (not just `overflow-hidden`) rounds the fill: the fill's
        // `dark:backdrop-blur` makes it ignore an ANCESTOR's border-radius +
        // overflow clip (Chromium paints the blurred backdrop as a square over
        // the rounded box), but `clip-path` DOES clip a backdrop-filter
        // descendant. Because it clips by position, the fill's corners get
        // trimmed by the card's corner curve as the bar rises into them — flat
        // top while partial, rounded once it reaches the top — with no JS guess.
        // `pointer-events-auto` opts the bar back in under the click-through bar grid.
        'group/outcome-bar pointer-events-auto relative isolate flex min-h-0 w-full min-w-0 flex-1 flex-col justify-end overflow-hidden rounded-control [clip-path:inset(0_round_var(--radius-control))]',
        c === undefined ? 'bg-background-tertiary' : c.outline,
      )}
      data-testid={`bar-box-${String(index)}`}
      data-original-index={outcome.originalIndex}
    >
      {href === '' ? null : (
        <Link
          href={href}
          prefetch={false}
          {...intent}
          aria-label={ariaLabel}
          onClick={onSelect}
          data-testid={`bar-box-link-${String(outcome.originalIndex)}`}
          className="absolute inset-0 z-overlay rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary"
        />
      )}
      {imageVariantActive ? (
        <BarBoxImageLayers
          index={index}
          originalIndex={outcome.originalIndex}
          pubkey={pubkey}
          hue={c?.hue}
          imageUrl={outcome.imageUrl}
          imageNsfw={outcome.imageNsfw ?? false}
          hasHovered={hasHovered}
          isHovered={isHovered}
          suppressOutcomeReveal={suppressOutcomeReveal}
          imageOverride={imageOverride}
        />
      ) : null}
      {/*
        Share-fill: a single div carrying both layout (absolute bottom-anchored,
        animated height) and visual (outcome-hue token, optional opacity/blur).
        Was previously a two-div nest (outer position + inner color) but the
        outer's `overflow-hidden` was redundant — the bar-box's `clip-path`
        rounds this child's corners (see the bar-box comment). Collapsed for the
        2026-05-24 /demo-mode DOM-reduction pass (~64 elements feed-wide).
        Token notes:
        - `bg-outcome-${hue}-fill`: light mode = opaque pale-100; dark mode =
          0.25-alpha vivid wash (token asymmetry by design).
        - `opacity-(--market-card-bar-fill-opacity)` (0.75 light / 0.9 dark)
          only attaches on the image variant so the image peeks through.
        - `hover:opacity-100` lifts the fill on hover; `dark:hover:...` reverts
          in dark mode (Tailwind variant order makes `dark:hover:` win inside
          `.dark`), keeping dark-mode hover signals on panel shadow + button.
        - `dark:backdrop-blur-[10px]` only registers in dark mode where the
          token is translucent enough for the blur to show; skipped in light
          mode to avoid a wasted backdrop-filter pass.
      */}
      <div
        aria-hidden="true"
        data-testid={`bar-fill-${String(index)}`}
        className={cn(
          'absolute bottom-0 inset-x-0 motion-reduce:transition-none',
          c === undefined && 'bg-background-tertiary',
          c?.fill,
          c !== undefined &&
            imageVariantActive &&
            'opacity-(--market-card-bar-fill-opacity) group-hover/outcome-bar:opacity-100 dark:group-hover/outcome-bar:opacity-(--market-card-bar-fill-opacity) dark:backdrop-blur-[10px]',
        )}
        style={{
          height: effectiveHeight,
          transition: 'height 600ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
      {/* Keep label contrast independent of image brightness and the animated fill height. */}
      {imageVariantActive ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-market-card-label-scrim"
        />
      ) : null}
      <div
        className={cn(
          'relative z-10 flex flex-col px-2 py-1',
          imageVariantActive && 'text-shadow-market-card-label',
        )}
      >
        <AnimatedOutcomePercent
          value={decimals ? outcome.share * 100 : pct}
          decimals={decimals}
          animateOnMount
          className={cn(
            'font-mono text-heading-lg leading-[24px] font-bold',
            c === undefined ? 'text-foreground-secondary' : c.text,
          )}
        />
        <AnimatePresence initial={false}>
          {isHovered ? (
            <m.div
              key="mcap"
              initial={reduceMotion === true ? { height: 0, opacity: 0 } : MCAP_INITIAL}
              animate={MCAP_ANIMATE}
              exit={reduceMotion === true ? { height: 0, opacity: 0 } : MCAP_EXIT}
              transition={reduceMotion === true ? { duration: 0 } : MOTION_HOVER}
              className="overflow-hidden"
              data-testid={`bar-mcap-slot-${String(index)}`}
            >
              <span
                className={cn(
                  'block pt-1 text-caption leading-[14px] font-medium',
                  c === undefined ? 'text-foreground-secondary' : c.text,
                )}
                data-testid={`bar-mcap-${String(index)}`}
              >
                {formatMcap(outcome.effectiveMarketCapD6)}
              </span>
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>
      {/* Live-trades overlay (spec 065) anchored INSIDE the bar-box so its
          bottom-right anchor lands on the bar, not on the button below. The
          tape is supplied by the feed router, so this overlay is purely
          presentational. */}
      <LiveTradesOverlay
        trades={liveTrades}
        liveSinceMountIds={liveSinceMountIds}
        outcomeIndex={outcome.originalIndex}
        colorClass={c?.text ?? 'text-foreground-secondary'}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardBarGrid — the outcome bar region (explicit props)
// ---------------------------------------------------------------------------

/** Stable empty-tape fallback — referenced by callers that pass no trades. */
const EMPTY_LIVE_TRADES: readonly LiveTrade[] = [];
/** Stable empty-projection fallback — for caller-owned preview/QA paths. */
const EMPTY_LIVE_IDS: readonly string[] = [];
const warnedMissingResolvedWinnerMarkets: Set<string> = new Set<string>();

interface CardBarGridProps {
  pubkey: string;
  /** Outcome summaries — caller guarantees length ≥ 2. */
  outcomes: OutcomeRowSummary[];
  consensusThreshold: string | bigint | null;
  position: number;
  /** Active feed sort — for click analytics. Omitted by the `/markets` feed,
   *  which reads it from `useMarketsSortStore` at click time instead. */
  activeSort?: string | undefined;
  liveTrades?: readonly LiveTrade[] | undefined;
  /** SDK-projected post-mount live trade ids — paired with `liveTrades`. */
  liveSinceMountIds?: readonly string[] | undefined;
  suppressOutcomeReveal: boolean;
  imageOverride: { params: PatternParams } | undefined;
  /** Winning outcome's original index for resolved/graduated markets — the
   *  label pills then paint that outcome as the winner and dim the rest.
   *  `null`/undefined for open markets (no winner treatment). */
  winnerOriginalIndex?: number | null | undefined;
  outcomeLabelPaddingClass?: string | undefined;
  /** See `MarketRowCardViewProps.linkDisabled` — empties every outcome pill's
   *  href so the pills render as non-navigating labels with no prefetch. */
  linkDisabled?: boolean;
  /** Enable FLIP position transitions. The feed keeps this off until it has
   *  observed a genuine post-mount trade, so cold cache hydration cannot
   *  animate outcomes through intermediate rankings. */
  animateOutcomeReorder: boolean;
}

/**
 * The bar-grid region — scroll / two-row variants, outcome colours,
 * percentages, live-trade overlays. This is the ONLY part of the card that
 * depends on `outcomes` (conviction); isolating it means a conviction tick
 * re-renders this and nothing else. Owns the colour + motion hooks and the
 * outcome-click handler.
 */
interface ScrollBarsProps {
  outcomes: readonly OutcomeRowSummary[];
  colorsFor: (o: OutcomeRowSummary) => OutcomeColorClasses | undefined;
  scaleDenominator: number;
  scrollStackRef: RefObject<HTMLDivElement | null>;
  scrollStackColumnClass: string;
  reduceMotion: boolean | null;
  showDecimals: boolean;
  imageVariantActive: boolean;
  pubkey: string;
  liveTrades: readonly LiveTrade[];
  liveSinceMountIds: readonly string[];
  formatMcap: (valueD6: string) => string;
  suppressOutcomeReveal: boolean;
  imageOverride: { params: PatternParams } | undefined;
  outcomeHref: (label: string) => string;
  onOutcomeSelect: () => void;
  resolvedStateFor: (originalIndex: number) => 'winner' | 'loser' | undefined;
  outcomeLabelPaddingClass?: string | undefined;
  animateOutcomeReorder: boolean;
}

// >3 outcomes — a single horizontally-scrolling row of bar+label columns, each
// pinned to (scrollport − gaps)/3 so exactly three are visible.
function ScrollBars({
  outcomes,
  colorsFor,
  scaleDenominator,
  scrollStackRef,
  scrollStackColumnClass,
  reduceMotion,
  showDecimals,
  imageVariantActive,
  pubkey,
  liveTrades,
  liveSinceMountIds,
  formatMcap,
  suppressOutcomeReveal,
  imageOverride,
  outcomeHref,
  onOutcomeSelect,
  resolvedStateFor,
  outcomeLabelPaddingClass,
  animateOutcomeReorder,
}: ScrollBarsProps): React.ReactNode {
  const tAria = useTranslations('market.row.aria');
  return (
    <div
      ref={scrollStackRef}
      className="flex h-full min-h-0 w-full min-w-0 gap-2 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scrollbar-none motion-reduce:scroll-auto"
    >
      {outcomes.map((o, i) => {
        const c = colorsFor(o);
        const pct = Math.round(o.share * 100);
        const scaledPct = Math.min(
          100,
          Math.max(0, Math.round((o.share / scaleDenominator) * 100)),
        );
        const heightPercent = `${String(scaledPct)}%`;
        return (
          <m.div
            key={o.originalIndex}
            layout={animateOutcomeReorder ? 'position' : false}
            transition={animateOutcomeReorder ? MOTION_POSITION : { duration: 0 }}
            className={cn(scrollStackColumnClass, 'relative')}
            data-testid={`column-${String(i)}`}
            data-original-index={o.originalIndex}
          >
            <MarketRowBarBox
              outcome={o}
              index={i}
              pct={pct}
              decimals={showDecimals}
              heightPercent={heightPercent}
              c={c}
              imageVariantActive={imageVariantActive}
              pubkey={pubkey}
              liveTrades={liveTrades}
              liveSinceMountIds={liveSinceMountIds}
              reduceMotion={reduceMotion}
              formatMcap={formatMcap}
              href={outcomeHref(o.label)}
              ariaLabel={tAria('outcomeButtonLabel', { outcome: o.label, share: pct })}
              onSelect={onOutcomeSelect}
              suppressOutcomeReveal={suppressOutcomeReveal}
              imageOverride={imageOverride}
            />
            <MarketRowOutcomeLabelButton
              label={o.label}
              originalIndex={o.originalIndex}
              c={c}
              ariaLabel={tAria('outcomeButtonLabel', { outcome: o.label, share: pct })}
              href={outcomeHref(o.label)}
              onSelect={onOutcomeSelect}
              resolvedState={resolvedStateFor(o.originalIndex)}
              labelPaddingClass={outcomeLabelPaddingClass}
            />
          </m.div>
        );
      })}
    </div>
  );
}

function CardBarGrid(props: CardBarGridProps): React.ReactNode {
  const {
    pubkey,
    outcomes,
    consensusThreshold,
    position,
    activeSort,
    liveTrades,
    liveSinceMountIds,
    suppressOutcomeReveal,
    imageOverride,
    winnerOriginalIndex,
    outcomeLabelPaddingClass,
    linkDisabled = false,
    animateOutcomeReorder,
  } = props;

  // Resolved-market label treatment: when a winner is known, the winning
  // outcome's pill keeps its outcome palette and gains a trophy while the rest
  // dim. A helper keeps the two label-button call sites (grid + scroll) in sync.
  const resolvedStateFor = (originalIndex: number): 'winner' | 'loser' | undefined => {
    if (winnerOriginalIndex === undefined || winnerOriginalIndex === null) return undefined;
    return originalIndex === winnerOriginalIndex ? 'winner' : 'loser';
  };

  // Normalise the optional caller props once — every outcome's overlay reads
  // this single tape + projection, so the per-outcome `LiveTradesOverlay`
  // no longer owns a live-trade subscription itself.
  //
  // The SDK-driven path (`FeedBarGrid`) ALWAYS passes both `liveTrades` AND
  // `liveSinceMountIds`. The explicit-props paths (launchpad preview,
  // workspace QA fixtures, unit tests) MAY pass only `liveTrades` — in those
  // contexts the caller knows every supplied trade is meant to animate
  // (they're synthetic / simulated / asserted-against), so we default the
  // projection to `liveTrades.map(t => t.id)`. A caller that explicitly
  // wants no bubbles passes `liveSinceMountIds={[]}`.
  const overlayTrades: readonly LiveTrade[] = liveTrades ?? EMPTY_LIVE_TRADES;
  const overlayLiveIds: readonly string[] =
    liveSinceMountIds ?? (liveTrades !== undefined ? liveTrades.map((t) => t.id) : EMPTY_LIVE_IDS);

  const reduceMotion = useReducedMotion();
  const shouldAnimateOutcomeReorder = animateOutcomeReorder && reduceMotion !== true;
  const tAria = useTranslations('market.row.aria');
  const { formatD6 } = useFormattedCurrency();
  const formatMcap = (valueD6: string): string => `${formatD6(valueD6, 'compactDecimal')} Mcap`;

  // Resolve hues by canonical outcome identity so reordered payloads keep the
  // same palette assignment across the grid and overflow scroll row.
  const allColorOutcomes = outcomes.map((o) => ({
    title: o.label,
    index: o.originalIndex,
  }));
  const allColorClasses = useOutcomeColor(allColorOutcomes);

  // Spec 062 — Outcome-image variant eligibility. All-or-nothing: the variant
  // activates iff every outcome carries a non-empty `imageUrl`. Pure derivation;
  // no analytics, no logs (FR-002, FR-015).
  const allOutcomesHaveImages: boolean = outcomes.every(
    (o) => typeof o.imageUrl === 'string' && o.imageUrl.length > 0,
  );
  const imageVariantActive: boolean = allOutcomesHaveImages;

  // Sort outcomes by descending share. ≤3: two flex rows (bars / buttons) share
  // `gap-2` so gutters align. >3: one flex row of bar+button columns so
  // `basis-[calc((100%-16px)/3)]` resolves against the scrollport (spec 060).
  const sortedOutcomes = outcomes.toSorted((a, b) => b.share - a.share);

  // Always render the percentage to 2-decimal precision (`.XX`) — keeps
  // near-tied outcomes distinguishable and the card visually consistent.
  // Skipped only for a brand-new market where every share is 0: there is
  // nothing to show and `0.00%` everywhere is just noise.
  const shareSum = sortedOutcomes.reduce((sum, o) => sum + o.share, 0);
  const showDecimals = shareSum > 0;
  // When the market has 4+ outcomes, cells are pinned to (card_width - gaps)/3
  // so exactly 3 are visible and the rest scroll. With ≤3 outcomes, cells
  // fluid-fill the card width edge-to-edge with no scroll.
  const useScrollMode = sortedOutcomes.length > 3;

  // Auto-scroll to leader when ranks flip + user is idle. The leader's
  // `originalIndex` is stable across re-renders, so the hook only triggers
  // when the front-most outcome genuinely changes (not on every render that
  // re-derives the sorted array).
  const scrollStackRef = useRef<HTMLDivElement | null>(null);
  const leaderOriginalIndex = sortedOutcomes[0]?.originalIndex ?? -1;
  useAutoScrollToLeader(scrollStackRef, leaderOriginalIndex, { enabled: useScrollMode });

  const scrollStackColumnClass = cn(
    'flex min-h-0 min-w-0 flex-col gap-2 snap-start basis-[calc((100%-16px)/3)] shrink-0 grow-0', // ARBITRARY: FR-018 — % resolves against one flex row = scrollport width
  );
  /** Cells in the two-row layout (≤3 outcomes): matching horizontal gaps between bars and buttons. */
  const twoRowCellClass = cn('flex min-h-0 min-w-0 snap-start', 'flex-1');

  // The color result is parallel to the input array, so map a sorted outcome
  // back to that array position via its canonical identity.
  function colorsFor(o: OutcomeRowSummary): (typeof allColorClasses)[number] | undefined {
    const inputPosition = outcomes.findIndex(
      (candidate) => candidate.originalIndex === o.originalIndex,
    );
    return inputPosition === -1 ? undefined : allColorClasses[inputPosition];
  }

  // Bars scale to the live TWAR consensus threshold. A share equal to the
  // threshold renders 100% of container height; shares above the threshold
  // (transient — market would resolve at that point) clamp to 100%. Defensive
  // fallback to a flat 1.0 scale when threshold is missing/invalid so the bar
  // never collapses to NaN.
  const thresholdShare = convictionFraction(consensusThreshold);
  const scaleDenominator = thresholdShare > 0 ? thresholdShare : 1;

  // Each outcome pill is its own `<Link>` (a `relative z-10` sibling above the
  // card's stretched-Link overlay), so there's no anchor-in-anchor and no
  // native-nav race to cancel. The pill navigates declaratively; this only
  // mirrors the whole-card click analytics for the outcome-scoped tap.
  // `linkDisabled` (synthetic pubkeys) empties the href — the pill then renders
  // as a non-navigating label with no prefetch (see MarketRowOutcomeLabelButton).
  function outcomeHref(label: string): string {
    if (linkDisabled) return '';
    return `/markets/${pubkey}?outcome=${encodeURIComponent(label)}`;
  }
  function handleOutcomeSelect(): void {
    trackEvent(MARKETS_CARD_CLICKED.name, {
      marketPubkey: pubkey,
      position,
      sort: activeSort ?? useMarketsSortStore.getState().activeSort,
    });
  }

  return (
    <div
      className={cn(
        // No `z-10` here: the OUTER wrapper's padding band (`px-1 pt-2`) must stay
        // BELOW the card's stretched-Link overlay so clicking it still navigates
        // (parity with the old card <Link> — the prior `handleCardClick` guard
        // only suppressed nav inside `data-overflow-scroll-zone`, not this band).
        // The `relative z-10` lives on that inner scroll-zone div instead.
        'flex min-h-36 flex-1 px-1 pt-2',
        // From md up the card is height-locked by its 4/3 aspect ratio, so the
        // bar region must shrink to fit (flex-1 + min-h-0) instead of insisting
        // on the 144 px mobile floor — otherwise content overflows the Panel's
        // overflow-hidden boundary and clips the footer.
        'md:min-h-0',
      )}
    >
      <div
        // Spec 065 — `overflow-x-auto` was previously here; CSS coerces
        // overflow-y to `auto` when one axis is `auto`, which clipped
        // live-trade bubbles drifting upward. Moved overflow to the
        // inner column row (only when useScrollMode=true) so this
        // wrapper retains `overflow: visible` for Y. Snap classes stay
        // here for spec-060 contract — they don't cause clipping.
        // `gap-2` lives here so the per-branch outer wrappers (≤3)
        // can return a Fragment instead of a redundant `flex-col gap-2 w-full`
        // div. scroll-mode has one child and ignores the gap; the ≤3
        // branch puts 2 children at this level and consumes it.
        // `relative z-10` lifts the scroll/swipe strip (and its outcome-bar and
        // outcome-pill Links) ABOVE the card's stretched-Link overlay. Bars and
        // pills select their outcome; `pointer-events-none` lets the gutters
        // between them fall through to whole-card nav (MOR-2224). Scrolling
        // still works — wheel and touch on a bar chain to the scroll row.
        className="pointer-events-none relative z-10 flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2 snap-x snap-mandatory scrollbar-none motion-reduce:scroll-auto"
        data-overflow-scroll-zone="true"
        data-testid="bar-grid"
        data-outcome-reorder-animation={shouldAnimateOutcomeReorder ? 'enabled' : 'disabled'}
      >
        {useScrollMode ? (
          <ScrollBars
            outcomes={sortedOutcomes}
            colorsFor={colorsFor}
            scaleDenominator={scaleDenominator}
            scrollStackRef={scrollStackRef}
            scrollStackColumnClass={scrollStackColumnClass}
            reduceMotion={reduceMotion}
            showDecimals={showDecimals}
            imageVariantActive={imageVariantActive}
            pubkey={pubkey}
            liveTrades={overlayTrades}
            liveSinceMountIds={overlayLiveIds}
            formatMcap={formatMcap}
            suppressOutcomeReveal={suppressOutcomeReveal}
            imageOverride={imageOverride}
            outcomeHref={outcomeHref}
            onOutcomeSelect={handleOutcomeSelect}
            resolvedStateFor={resolvedStateFor}
            outcomeLabelPaddingClass={outcomeLabelPaddingClass}
            animateOutcomeReorder={shouldAnimateOutcomeReorder}
          />
        ) : (
          // Outer `flex-col gap-2 w-full` hoisted to the bar-grid parent;
          // see the wrapper at the top of CardBarGrid. Fragment here.
          <>
            <div className="flex min-h-0 w-full min-w-0 flex-1 gap-2">
              {sortedOutcomes.map((o, i) => {
                const c = colorsFor(o);
                const pct = Math.round(o.share * 100);
                const scaledPct = Math.min(
                  100,
                  Math.max(0, Math.round((o.share / scaleDenominator) * 100)),
                );
                const heightPercent = `${String(scaledPct)}%`;
                return (
                  <m.div
                    key={o.originalIndex}
                    layout={shouldAnimateOutcomeReorder ? 'position' : false}
                    transition={shouldAnimateOutcomeReorder ? MOTION_POSITION : { duration: 0 }}
                    className={cn(twoRowCellClass, 'relative')}
                    data-testid={`column-${String(i)}`}
                    data-original-index={o.originalIndex}
                  >
                    <MarketRowBarBox
                      outcome={o}
                      index={i}
                      pct={pct}
                      decimals={showDecimals}
                      heightPercent={heightPercent}
                      c={c}
                      imageVariantActive={imageVariantActive}
                      pubkey={pubkey}
                      liveTrades={overlayTrades}
                      liveSinceMountIds={overlayLiveIds}
                      reduceMotion={reduceMotion}
                      formatMcap={formatMcap}
                      href={outcomeHref(o.label)}
                      ariaLabel={tAria('outcomeButtonLabel', {
                        outcome: o.label,
                        share: pct,
                      })}
                      onSelect={handleOutcomeSelect}
                      suppressOutcomeReveal={suppressOutcomeReveal}
                      imageOverride={imageOverride}
                    />
                  </m.div>
                );
              })}
            </div>
            <div className="flex w-full min-w-0 shrink-0 gap-2">
              {sortedOutcomes.map((o, i) => {
                const c = colorsFor(o);
                const pct = Math.round(o.share * 100);
                return (
                  <m.div
                    key={o.originalIndex}
                    layout={shouldAnimateOutcomeReorder ? 'position' : false}
                    transition={shouldAnimateOutcomeReorder ? MOTION_POSITION : { duration: 0 }}
                    className={twoRowCellClass}
                    data-testid={`column-button-${String(i)}`}
                    data-original-index={o.originalIndex}
                  >
                    <MarketRowOutcomeLabelButton
                      label={o.label}
                      originalIndex={o.originalIndex}
                      c={c}
                      ariaLabel={tAria('outcomeButtonLabel', {
                        outcome: o.label,
                        share: pct,
                      })}
                      href={outcomeHref(o.label)}
                      onSelect={handleOutcomeSelect}
                      resolvedState={resolvedStateFor(o.originalIndex)}
                      labelPaddingClass={outcomeLabelPaddingClass}
                    />
                  </m.div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardBookmark — watchlist read + mutation hooks, isolated into its own leaf.
// ---------------------------------------------------------------------------

/**
 * The bookmark control, split out of `CardFooter` so the three watchlist hooks
 * (`useGetWatchlist` + the two mutations) do NOT re-execute on every
 * `volumeTotal` / `prizePool` patch. `CardFooter` re-renders on each money tick;
 * `pubkey` + `watchKind` are value-stable, so the React Compiler bails this
 * child across those re-renders. `CardBookmark` then re-renders only on a real
 * watchlist change — measured as the #2 feed re-render offender before this
 * split.
 *
 * `watchKind` is passed in (not read here via `useGetMarketsField`) so the
 * explicit-props `MarketRowCardView` — rendered OUTSIDE the feed `<Items>`
 * context (launchpad creation preview, similar-markets, workspace showcase) —
 * never calls a field hook that throws when there's no `ListContext`. The feed
 * derives it from the row status it already reads in `FeedFooter`.
 */
function CardBookmark(props: { pubkey: string; watchKind: WatchlistKind }): React.ReactNode {
  const { pubkey, watchKind } = props;

  const tBookmark = useTranslations('market.row.bookmark');
  const tWatch = useTranslations('common.watchlist');

  const { data: watchlist } = useAuthenticatedWatchlist({
    kind: watchKind,
    fields: ['pubkeys'],
  });
  const { ensureAuthenticated } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  const isSaved = watchlist?.pubkeys?.includes(pubkey) ?? false;

  function handleBookmarkClick(): void {
    // The bookmark sits at `relative z-10` (below), ABOVE the card's
    // stretched-Link overlay, so its click never reaches the overlay anchor —
    // no preventDefault/stopPropagation gymnastics needed; it just toggles.
    // Logged-out users have no watchlist — open the auth gate instead.
    if (!ensureAuthenticated()) return;
    if (isSaved) {
      removeFromWatchlist.mutate({ kind: watchKind, pubkey });
    } else {
      addToWatchlist.mutate({ kind: watchKind, pubkey });
    }
    showToast({
      id: isSaved ? TOAST_IDS.WATCHLIST_REMOVED : TOAST_IDS.WATCHLIST_SAVED,
      message: isSaved ? tWatch('removed') : tWatch('added'),
      variant: 'success',
    });
    trackEvent(MARKETS_BOOKMARK_TOGGLED.name, {
      marketPubkey: pubkey,
      saved: !isSaved,
    });
  }

  return (
    <span data-testid="bookmark-slot" className="relative z-10">
      <AnimatedBookmark
        isSaved={isSaved}
        onClick={handleBookmarkClick}
        ariaLabel={isSaved ? tBookmark('remove') : tBookmark('save')}
        className="ml-auto"
        size={14}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// CardFooter — resolves / traded / prize-pool row + bookmark (explicit props)
// ---------------------------------------------------------------------------

interface CardFooterProps {
  pubkey: string;
  /** Watchlist view the bookmark reads/writes — derived from the row status by
   *  the feed (`FeedFooter`); `'markets'` for the explicit-props variant. */
  watchKind: WatchlistKind;
  consensusThreshold: string | bigint | null;
  thresholdSegment?: ReactNode;
  volumeTotal: string;
  prizePool: string;
  /** True when the market is resolved or graduated — the footer then reads
   *  "<when> · <traded> · <pool>" instead of "Resolves X% · traded · pool". */
  isResolved?: boolean;
  /** Resolution timestamp (ISO) — drives the relative resolution-age label.
   *  Only read when `isResolved` is true. */
  resolvedAt?: string | null;
  /** Creation timestamp (ISO) — drives the trailing "<age> old" bullet on the
   *  open-market footer. Omitted for synthetic/preview cards (no real age). */
  createdAt?: string | null;
  liveEvent?: MarketRowCardLiveEvent;
}

/**
 * Card footer: the resolution, volume, prize, and age metadata plus bookmark.
 * button. Isolated so a `volumeTotal` / `prizePool` tick re-renders this region
 * only — never the bar grid or the chrome. The watchlist hooks live in the
 * separate `CardBookmark` leaf so a money tick re-rendering this row does not
 * re-execute them.
 */
function CardFooter(props: CardFooterProps): React.ReactNode {
  const {
    pubkey,
    watchKind,
    consensusThreshold,
    thresholdSegment,
    volumeTotal,
    prizePool,
    isResolved,
    resolvedAt,
    createdAt,
    liveEvent,
  } = props;

  const tFooter = useTranslations('market.row');
  const tTimeIntl = useTranslations('time');
  const tLiveCountdown = useTranslations('liveEvents.countdown');
  const tEventTime = useTranslations('liveEvents.eventTimeCard');
  const { formatD6 } = useFormattedCurrency();

  // Resolved/graduated markets replace the no-longer-relevant conviction
  // threshold with the resolution age, while retaining traded volume and the
  // final prize pool. `formatRelativeTime` mirrors the winner-feed row.
  if (isResolved === true) {
    const resolvedLabel = formatRelativeTime(
      resolvedAt ?? '',
      (key: string, params?: Record<string, number>): string =>
        tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params),
      { style: 'long' },
    );
    return (
      <div className="flex shrink-0 items-center gap-1 pl-2">
        <p className="flex min-w-0 flex-1 items-center gap-0.5 text-caption text-foreground-tertiary">
          <span className="text-feedback-success-content inline-flex shrink-0 items-center gap-1">
            <Icon icon={CheckmarkCircle02IconSolid} className="size-3" aria-hidden="true" />
            {/* The checkmark carries the visible resolution cue, so show only
                the age. Preserve the full status phrase for screen readers. */}
            <span className="sr-only">
              {resolvedLabel === ''
                ? tFooter('resolved')
                : tFooter('resolved_at', { value: resolvedLabel })}
            </span>
            <span aria-hidden="true">
              {resolvedLabel === '' ? tFooter('resolved') : resolvedLabel}
            </span>
          </span>
          <span aria-hidden="true" className="w-2.5 text-center shrink-0">
            {'·'}
          </span>
          <span className="inline-flex min-w-0 items-center gap-0.5">
            <Icon
              icon={ArrowDataTransferHorizontalIconSolid}
              className="size-3"
              aria-hidden="true"
            />
            <span className="sr-only">
              {tFooter('traded', { value: formatD6(volumeTotal, 'compactDecimal') })}
            </span>
            <span aria-hidden="true" className="truncate">
              {formatD6(volumeTotal, 'compactDecimal')}
            </span>
          </span>
          <span aria-hidden="true" className="w-2.5 text-center shrink-0">
            {'·'}
          </span>
          <span className="inline-flex min-w-0 items-center gap-0.5">
            <Icon icon={ChampionIconSolid} className="size-3" aria-hidden="true" />
            <span className="sr-only">{tFooter('prizePoolLabel')}</span>
            <span aria-hidden="true" className="truncate">
              {formatD6(prizePool, 'compactDecimal')}
            </span>
          </span>
        </p>
        <CardBookmark pubkey={pubkey} watchKind={watchKind} />
      </div>
    );
  }

  const resolutionSegment =
    liveEvent === undefined ? (
      (thresholdSegment ?? <CardThresholdSegment consensusThreshold={consensusThreshold} />)
    ) : (
      <span
        className="inline-flex min-w-0 items-center gap-0.5 font-mono font-semibold text-feedback-error-content @max-[419px]/market-row:hidden"
        data-testid="live-event-resolution-countdown"
      >
        <Icon icon={Timer01Icon} className="size-3" aria-hidden="true" />
        <Countdown
          targetMs={Date.parse(liveEvent.event.end)}
          initialNowMs={liveEvent.initialNowMs}
          endedLabel={tLiveCountdown('ended')}
          ariaLabel={(countdown: string): string => tEventTime('countdownLabel', { countdown })}
          className="truncate tabular-nums"
        />
      </span>
    );
  const openFooterMoneyFormat = liveEvent === undefined ? 'compactDecimal' : 'compact';
  const formattedVolume = formatD6(volumeTotal, openFooterMoneyFormat);
  const formattedPrizePool = formatD6(prizePool, openFooterMoneyFormat);

  // Trailing age bullet (clock icon + e.g. "2hrs") from the creation timestamp.
  // `formatRelativeTime` returns '' for a null/empty `createdAt` (preview cards),
  // so the bullet is dropped entirely rather than reading a dangling separator.
  // The feed becomes a compact three-column grid at `lg`, so age stays hidden
  // through that range and returns only once each card has `xl` viewport room.
  const ageLabel = formatRelativeTime(
    liveEvent?.event.start ?? createdAt ?? '',
    (key: string, params?: Record<string, number>): string =>
      tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params),
    {
      style: 'age',
      ...(liveEvent === undefined ? {} : { nowMs: liveEvent.initialNowMs }),
    },
  );

  return (
    <div className="flex shrink-0 items-center gap-1 pl-2">
      <p className="flex min-w-0 flex-1 items-center gap-0.5 text-caption text-foreground-tertiary">
        {resolutionSegment}
        <span
          aria-hidden="true"
          className={cn(
            'w-2.5 text-center shrink-0',
            liveEvent !== undefined && '@max-[419px]/market-row:hidden',
          )}
        >
          {'·'}
        </span>
        {/* Each metadata segment leads with an icon instead of a trailing word
            ("traded" / "pool" / "old") to save row width; the word is kept as an
            sr-only label so screen readers still announce it. */}
        <span
          className={cn(
            'inline-flex items-center gap-0.5 min-w-0',
            liveEvent !== undefined && '@max-[339px]/market-row:hidden',
          )}
        >
          <Icon icon={ArrowDataTransferHorizontalIconSolid} className="size-3" aria-hidden="true" />
          <span className="sr-only">{tFooter('traded', { value: formattedVolume })}</span>
          <span aria-hidden="true" className="truncate">
            {formattedVolume}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'w-2.5 text-center shrink-0',
            liveEvent !== undefined && '@max-[339px]/market-row:hidden',
          )}
        >
          {'·'}
        </span>
        <span className="inline-flex items-center gap-0.5 min-w-0">
          <Icon icon={ChampionIconSolid} className="size-3" aria-hidden="true" />
          <span className="sr-only">{tFooter('prizePoolLabel')}</span>
          <span aria-hidden="true" className="truncate">
            {formattedPrizePool}
          </span>
        </span>
        {ageLabel === '' ? null : liveEvent === undefined ? (
          <>
            <span aria-hidden="true" className="w-2.5 text-center shrink-0 md:hidden xl:inline">
              {'·'}
            </span>
            <span className="inline-flex items-center gap-0.5 shrink-0 whitespace-nowrap md:hidden xl:inline-flex">
              <Icon icon={Plant04IconSolid} className="size-3" aria-hidden="true" />
              <span className="sr-only">{tFooter('ageLabel')}</span>
              <span aria-hidden="true">{ageLabel}</span>
            </span>
          </>
        ) : (
          <>
            <span aria-hidden="true" className="w-2.5 text-center shrink-0">
              {'·'}
            </span>
            <span className="inline-flex items-center gap-0.5 shrink-0 whitespace-nowrap">
              <Icon icon={Plant04IconSolid} className="size-3" aria-hidden="true" />
              <span className="sr-only">{tFooter('ageLabel')}</span>
              <span aria-hidden="true">{ageLabel}</span>
            </span>
          </>
        )}
      </p>
      <CardBookmark pubkey={pubkey} watchKind={watchKind} />
    </div>
  );
}

function CardThresholdSegment(props: { consensusThreshold: string | bigint | null }): ReactNode {
  const tFooter = useTranslations('market.row');
  // A missing config is still loading; a real zero remains formatted normally.
  const thresholdLabel =
    props.consensusThreshold === null
      ? tFooter('threshold_loading')
      : `${Math.round(convictionPercent(props.consensusThreshold))}%`;

  return (
    <span className="inline-flex min-w-0 items-center gap-0.5">
      <Icon icon={Target02IconSolid} className="size-3" aria-hidden="true" />
      <span className="sr-only">{tFooter('resolves_at', { value: thresholdLabel })}</span>
      <span aria-hidden="true" className="truncate">
        {thresholdLabel}
      </span>
    </span>
  );
}

function FeedThresholdSegment(): ReactNode {
  const consensusThreshold = useFeedMarketThreshold();
  return <CardThresholdSegment consensusThreshold={consensusThreshold} />;
}

// ---------------------------------------------------------------------------
// MarketRowCardChrome — <Link>/<Panel>/title row, slots bar grid + footer
// ---------------------------------------------------------------------------

interface MarketRowCardChromeProps {
  outcomeLayout?: 'bars' | 'stacked';
  surface?: 'card' | 'list';
  onOpenMarket?: (() => void) | undefined;
  /** Origin mark rendered on the cover image corner (influencer handshake / Morfi logomark). */
  identity?: ReactNode;
  /** Wraps the Panel in the animated identity beam for influencer/Featured markets. */
  beam?: (panel: ReactNode) => ReactNode;
  imageBeam?: ((image: ReactNode) => ReactNode) | undefined;
  pubkey: string;
  title: string;
  coverImageUrl: string;
  coverImageNsfw: boolean;
  location: string | null;
  category: string | null;
  position: number;
  /** Active feed sort — for click analytics. Omitted by the `/markets` feed,
   *  which reads it from `useMarketsSortStore` at click time instead. */
  activeSort?: string | undefined;
  imageOverride: { params: PatternParams } | undefined;
  /** The bar-grid region — `CardBarGrid` (preview/QA) or `FeedBarGrid` (feed). */
  barGrid: ReactNode;
  /** The footer region — `CardFooter` (preview/QA) or `FeedFooter` (feed). */
  footer: ReactNode;
  /** Optional anchor target — forwards to the card's `<Link>`. */
  target?: string | undefined;
  /** Optional anchor rel. */
  rel?: string | undefined;
  /** LCP: above-the-fold cards eager-load their cover thumbnail. See MOR-566. */
  priorityImage?: boolean;
  /** See `MarketRowCardViewProps.linkDisabled` — synthetic pubkeys must not
   *  render the stretched nav <Link> or fire route/data prefetches. */
  linkDisabled?: boolean;
}

/**
 * Static card chrome: the `<Link>` + `<Panel>` + title row. Reads no volatile
 * market field, so when its parent does not re-render (the feed shell reads
 * only stable fields) this never re-renders on a trade — the `barGrid` /
 * `footer` nodes keep identity and each region re-renders on its own.
 */
function MarketRowCardChrome(props: MarketRowCardChromeProps): React.ReactNode {
  const {
    pubkey,
    title,
    coverImageUrl,
    coverImageNsfw,
    location,
    category,
    position,
    activeSort,
    imageOverride,
    barGrid,
    footer,
    target,
    rel,
    priorityImage = false,
    linkDisabled = false,
    outcomeLayout = 'bars',
    surface = 'card',
    onOpenMarket,
  } = props;

  const tAria = useTranslations('market.row.aria');
  const locationBadgeEnabled = useFeatureGate('region');

  const cardHref = `/markets/${pubkey}`;
  const flag = location !== null ? getLocationFlag(location) : null;
  const badgeContent = flag ?? '🌐';
  const image = (
    <OptimizedImage
      variant="thumbnail"
      entity={{
        kind: 'market',
        pubkey,
        category: (category ?? '').toLowerCase(),
        coverImageUrl: coverImageUrl.length > 0 ? coverImageUrl : null,
        nsfw: coverImageNsfw,
      }}
      alt=""
      size="size-12"
      rounded="rounded-control"
      // ALLOW-NO-PREVIEW: the card owns navigation; an image preview would steal it.
      preview={false}
      priority={priorityImage}
      {...(imageOverride !== undefined ? { imageOverride } : {})}
    />
  );

  // The full route prefetch already contains the SSR-dehydrated market and
  // lifecycle-correct chart entries. A second browser data-warm raced that
  // payload on high-outcome markets, so the last in-flight outcome could be
  // requested again when the destination hydrated.
  const intent = useIntentRoutePrefetch(cardHref);

  function handleCardSelect(): void {
    trackEvent(MARKETS_CARD_CLICKED.name, {
      marketPubkey: pubkey,
      position,
      sort: activeSort ?? useMarketsSortStore.getState().activeSort,
    });
  }

  return (
    <LazyMotion features={domAnimation}>
      {(props.beam ?? ((panel: ReactNode): ReactNode => panel))(
        <Panel
          surface={surface === 'list' ? 'none' : 'solid'}
          inheritBackground={surface === 'list'}
          elevation="flat"
          data-testid="market-row-card"
          className={cn(
            '@container/market-row relative flex min-h-0 w-full min-w-0 flex-col',
            outcomeLayout === 'bars' && 'h-full gap-1 cursor-pointer overflow-hidden',
            outcomeLayout === 'bars' && ASPECT_RATIO_CLASS,
            outcomeLayout === 'stacked' && 'gap-4 cursor-pointer',
            surface === 'list' ? 'p-0' : outcomeLayout === 'stacked' ? 'p-4' : 'p-2',
            surface === 'card' &&
              'transition-[box-shadow,border-color] duration-fast ease-default motion-reduce:transition-none hover:border-border-primary hover:shadow-elevation-card focus-within:border-border-primary focus-within:shadow-elevation-card',
          )} /* min-h-0 + min-w-0 + gap-1 (overrides Panel default gap-6) so the bar grid flex-1 receives a real height in grid rows; no max-width — feed grids own column width. Spec 069: aspect ratio at md/lg via ASPECT_RATIO_CLASS. */
        >
          {/*
          Stretched-Link overlay → whole-card soft-nav to the market with
          viewport RSC + SDK-cache prefetch (`onMouseEnter`/`onFocus` intent).
          Replaces the old <Link> that WRAPPED the card. Rendered FIRST so it is
          the card's first focusable element (card → outcome bars → outcome pills
          → bookmark tab order); at `z-0` it captures clicks across non-interactive content
          (a positioned `z-0` element paints above the non-positioned title /
          footer regardless of DOM order) while the `relative z-10` bar grid
          (bar and pill taps select outcomes; gaps fall through), bookmark, and
          per-outcome <Link>s stay above it, without anchor-in-anchor.
          `MarketRowCardChrome` still reads no volatile field, so
          re-render isolation is unchanged.
        */}
          {/* prefetch={false}: a feed renders dozens of cards, so a viewport
            (auto) route prefetch per card is a mount storm of shell requests
            that contends with the feed's own first paint. The `{...intent}`
            hover/focus/touch warming below prefetches the complete detail RSC
            on real intent. Only the handful of always-visible NAV anchors keep
            a literal full-RSC prefetch. */}
          {onOpenMarket !== undefined ? (
            <AnimatedButton
              variant="tertiary"
              aria-label={tAria('cardLabel', { title })}
              onClick={onOpenMarket}
              disableHoverScale
              disableTapScale
              data-testid="market-row-card-link"
              className="absolute inset-0 z-0 h-auto w-auto rounded-[inherit] hover:bg-transparent"
            />
          ) : linkDisabled ? null : (
            <Link
              href={cardHref}
              prefetch={false}
              aria-label={tAria('cardLabel', { title })}
              onClick={handleCardSelect}
              {...intent}
              {...(target !== undefined ? { target } : {})}
              {...(rel !== undefined ? { rel } : {})}
              data-testid="market-row-card-link"
              className="absolute inset-0 z-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary"
            />
          )}
          {/* flex-1 lets the panel absorb extra card height when siblings (e.g. taller cards in the same row) push the row taller. */}
          <div
            className={cn(
              'flex min-h-0 flex-col',
              outcomeLayout === 'stacked' ? 'gap-6' : 'flex-1',
            )}
          >
            {/* Title row */}
            <div
              className={cn(
                'flex shrink-0 items-center gap-3',
                outcomeLayout === 'stacked' && 'pointer-events-none relative z-10',
                outcomeLayout === 'bars' && surface === 'card' && 'p-1',
              )}
            >
              {/* Positioned, so it would paint above the `z-0` card link and
                  swallow clicks (MOR-2224). Only the NSFW tap-to-reveal
                  control (`role=button`) stays clickable. */}
              <div className="pointer-events-none relative size-12 shrink-0 rounded-control [&_[role=button]]:pointer-events-auto">
                {props.imageBeam === undefined ? image : props.imageBeam(image)}
                {locationBadgeEnabled === true ? (
                  <span
                    aria-hidden="true"
                    data-testid="market-location-badge"
                    className="absolute -right-1 -bottom-1 inline-flex size-5 items-center justify-center rounded-full bg-background-primary border border-border-primary text-caption leading-none font-mono"
                    title={location ?? undefined}
                  >
                    {badgeContent}
                  </span>
                ) : null}
                {props.identity}
              </div>
              <h3
                className="text-body-lg leading-[20px] font-semibold line-clamp-2 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden text-foreground-primary tracking-[-0.18px] flex-1 min-w-0"
                title={title}
              >
                {title}
              </h3>
            </div>

            {/*
            ≤3 outcomes: two flex rows (bars / buttons) share one `gap-2` so
            horizontal gutters line up. >3 outcomes: one flex row of bar+button
            columns — two stacked rows broke `%` basis (calc((100%-16px)/3))
            against `w-max` and overflow clipping. Spec 060 FR-018 / FR-009.
          */}
            {barGrid}
          </div>

          {/* Footer */}
          {footer}
        </Panel>,
      )}
    </LazyMotion>
  );
}

// ---------------------------------------------------------------------------
// MarketRowCardView — explicit-props card for synthetic / not-yet-on-chain
// markets (launchpad creation preview, workspace QA showcase).
// ---------------------------------------------------------------------------

export function MarketRowCardView(props: MarketRowCardViewProps): React.ReactNode | null {
  const {
    pubkey,
    title,
    coverImageUrl,
    coverImageNsfw = false,
    location,
    category,
    volumeTotal,
    prizePool,
    outcomes,
    position,
    activeSort,
    consensusThreshold,
    liveTrades,
    liveSinceMountIds,
    suppressOutcomeReveal = false,
    imageOverride,
    target,
    rel,
    outcomeLabelPaddingClass,
    linkDisabled = false,
    liveEvent,
    resolved,
    outcomeLayout = 'bars',
    surface = 'card',
    onSelectOutcome,
    onOpenMarket,
    bookmark,
    identityPreview = false,
  } = props;

  // Render guard — markets with fewer than 2 outcomes don't render.
  if (outcomes.length < 2) {
    return null;
  }

  return (
    <MarketRowCardChrome
      outcomeLayout={outcomeLayout}
      surface={surface}
      onOpenMarket={onOpenMarket}
      pubkey={pubkey}
      title={title}
      coverImageUrl={coverImageUrl}
      coverImageNsfw={coverImageNsfw}
      location={location}
      category={category}
      position={position}
      activeSort={activeSort}
      imageOverride={imageOverride}
      identity={<OriginMark marketType={props.identity?.marketType} preview={identityPreview} />}
      imageBeam={
        surface === 'list'
          ? (image: ReactNode): ReactNode => (
              <IdentityBeam
                marketType={props.identity?.marketType}
                radius={12}
                preview={identityPreview}
              >
                {image}
              </IdentityBeam>
            )
          : undefined
      }
      beam={(panel: ReactNode): ReactNode =>
        surface === 'list' ? (
          panel
        ) : outcomeLayout === 'stacked' &&
          (props.identity?.marketType === undefined || props.identity.marketType === 'ordinary') ? (
          <MarketOutcomeHover
            outcomes={outcomes}
            winnerOriginalIndex={resolved?.winnerOriginalIndex}
          >
            {panel}
          </MarketOutcomeHover>
        ) : (
          <IdentityBeam
            marketType={props.identity?.marketType}
            className="h-full"
            preview={identityPreview}
          >
            {panel}
          </IdentityBeam>
        )
      }
      linkDisabled={linkDisabled}
      {...(target !== undefined ? { target } : {})}
      {...(rel !== undefined ? { rel } : {})}
      barGrid={
        outcomeLayout === 'stacked' ? (
          <OutcomeStack
            pubkey={pubkey}
            outcomes={outcomes}
            consensusThreshold={consensusThreshold}
            winnerOriginalIndex={resolved?.winnerOriginalIndex}
            linkDisabled={linkDisabled}
            onSelectOutcome={onSelectOutcome}
          />
        ) : (
          <CardBarGrid
            pubkey={pubkey}
            outcomes={outcomes}
            consensusThreshold={consensusThreshold}
            position={position}
            activeSort={activeSort}
            liveTrades={liveTrades}
            liveSinceMountIds={liveSinceMountIds}
            suppressOutcomeReveal={suppressOutcomeReveal}
            imageOverride={imageOverride}
            outcomeLabelPaddingClass={outcomeLabelPaddingClass}
            linkDisabled={linkDisabled}
            winnerOriginalIndex={resolved?.winnerOriginalIndex}
            animateOutcomeReorder
          />
        )
      }
      footer={
        outcomeLayout === 'stacked' ? (
          <StackedMarketCardFooter
            pubkey={pubkey}
            outcomeCount={outcomes.length}
            prizePool={prizePool}
            threshold={<CardThresholdSegment consensusThreshold={consensusThreshold} />}
            bookmark={bookmark ?? <CardBookmark pubkey={pubkey} watchKind="markets" />}
            linkDisabled={linkDisabled}
            onOpenMarket={onOpenMarket}
          />
        ) : (
          <CardFooter
            pubkey={pubkey}
            watchKind="markets"
            consensusThreshold={consensusThreshold}
            volumeTotal={volumeTotal}
            prizePool={prizePool}
            isResolved={resolved !== undefined}
            {...(resolved === undefined ? {} : { resolvedAt: resolved.resolvedAt })}
            {...(liveEvent === undefined ? {} : { liveEvent })}
          />
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Feed field-isolation leaves — each reads ONLY the fields its region shows.
// A conviction tick re-renders FeedBarGrid; a volume/prize tick re-renders
// FeedFooter; neither touches the chrome. See the block comment at the top.
// ---------------------------------------------------------------------------

interface FeedRegionProps {
  pubkey: string;
  activeSort?: string | undefined;
}

/**
 * Feed bar grid — reads `outcomes` (conviction) + `consensusThreshold` and
 * renders `CardBarGrid`. This is the leaf that re-renders on every conviction
 * patch; the chrome and footer do not.
 *
 * It also selects this card's tape from the feed-level router. The external
 * store notifies only the market that received a trade, while retaining a
 * bounded tape for cards that are currently outside the viewport.
 */
function FeedBarGrid(props: FeedRegionProps): React.ReactNode {
  const { pubkey, activeSort } = props;
  const { trades: liveTrades, liveSinceMountIds } = useMarketFeedTrades(pubkey);
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const outcomes = useGetMarketsField(pubkey, 'outcomes');
  const consensusThreshold = useFeedMarketThreshold();
  // Stable fields — only meaningful once the market resolves; reading them
  // here drives the winning-outcome label highlight without per-trade churn.
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const status = useGetMarketsField(pubkey, 'status');
  const convictionUpdatedAt = useGetMarketsField(pubkey, 'convictionUpdatedAt');
  const outcomeCount = useGetMarketsField(pubkey, 'outcomeCount');
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const winnerOutcomeIndex = useGetMarketsField(pubkey, 'winnerOutcomeIndex');
  const summaries: OutcomeRowSummary[] =
    outcomes !== undefined
      ? buildOutcomeRowSummaries(outcomes, {
          status: status ?? undefined,
          convictionUpdatedAt,
          outcomeCount,
        })
      : [];
  const isResolved = isMarketResolved(status ?? '');
  const hasCanonicalWinner =
    winnerOutcomeIndex !== undefined &&
    winnerOutcomeIndex !== null &&
    summaries.some((outcome) => outcome.originalIndex === winnerOutcomeIndex);
  const winnerOriginalIndex = isResolved && hasCanonicalWinner ? winnerOutcomeIndex : null;
  const availableOutcomeIndicesKey = summaries
    .map((outcome) => String(outcome.originalIndex))
    .join(',');

  useEffect(() => {
    if (
      !isResolved ||
      winnerOutcomeIndex === undefined ||
      winnerOutcomeIndex === null ||
      hasCanonicalWinner ||
      availableOutcomeIndicesKey === '' ||
      warnedMissingResolvedWinnerMarkets.has(pubkey)
    ) {
      return;
    }

    warnedMissingResolvedWinnerMarkets.add(pubkey);
    logger.warn('market_feed.resolved_winner_outcome_missing', {
      marketPubkey: pubkey,
      status: status ?? '',
      winnerOutcomeIndex,
      availableOutcomeIndices: availableOutcomeIndicesKey.split(',').map(Number),
    });
  }, [
    availableOutcomeIndicesKey,
    hasCanonicalWinner,
    isResolved,
    pubkey,
    status,
    winnerOutcomeIndex,
  ]);

  return (
    <CardBarGrid
      pubkey={pubkey}
      outcomes={summaries}
      consensusThreshold={consensusThreshold}
      position={0}
      activeSort={activeSort}
      liveTrades={liveTrades}
      liveSinceMountIds={liveSinceMountIds}
      suppressOutcomeReveal={false}
      imageOverride={undefined}
      winnerOriginalIndex={winnerOriginalIndex}
      animateOutcomeReorder={liveSinceMountIds.length > 0}
    />
  );
}

/**
 * Feed footer — reads `volumeTotal` / `prizePool` and
 * renders `CardFooter`. Re-renders on a volume/prize patch; the chrome and bar
 * grid do not.
 */
function FeedFooter(props: {
  pubkey: string;
  liveEvent?: MarketRowCardLiveEvent;
}): React.ReactNode {
  const { pubkey, liveEvent } = props;
  // ALLOW-UNSCOPED: per-field leaf hooks — each field name is the positional arg, already scoped to one field.
  const volumeTotal = useGetMarketsField(pubkey, 'volumeTotal');
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const prizePool = useGetMarketsField(pubkey, 'prizePool');
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  // `status` / `resolvedAt` are stable (change only when a market resolves —
  // a one-off re-render of this leaf), so reading them here adds no per-trade
  // churn. They switch the footer to its "Resolved <when>" variant.
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const status = useGetMarketsField(pubkey, 'status');
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const resolvedAt = useGetMarketsField(pubkey, 'resolvedAt');
  // `createdAt` is set at creation and never changes — reading it here adds no
  // per-trade churn. Drives the open-market footer's trailing "<age> old" bullet.
  // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
  const createdAt = useGetMarketsField(pubkey, 'createdAt');
  const isResolved = isMarketResolved(status ?? '');
  // Bookmark against the watchlist view matching the row's CURRENT status
  // (markets→trading/resolved, winners→graduated). `status` is a stable field
  // already read above, so deriving the kind here adds no per-trade churn.
  const watchKind = watchlistKindForStatus(status);

  return (
    <CardFooter
      pubkey={pubkey}
      watchKind={watchKind}
      consensusThreshold={null}
      thresholdSegment={<FeedThresholdSegment />}
      volumeTotal={volumeTotal ?? '0'}
      prizePool={prizePool ?? '0'}
      isResolved={isResolved}
      resolvedAt={resolvedAt ?? null}
      createdAt={createdAt ?? null}
      {...(liveEvent === undefined ? {} : { liveEvent })}
    />
  );
}

// ---------------------------------------------------------------------------
// Leaf shell — the component rendered inside the `/markets` feed `<Items>`.
// Reads ONLY stable market fields (title, cover, category, location,
// outcomeCount) so it never re-renders on a trade; the volatile fields are
// read by `FeedBarGrid` / `FeedFooter`. `FeedBarGrid` selects this market's
// bounded tape from the feed-level router for the live-trade overlays.
// ---------------------------------------------------------------------------

export interface MarketRowCardProps {
  /** Market pubkey — supplied by the feed `<Items>` render-prop. */
  pubkey: string;
  /** Active sort key — forwarded to card-click analytics. The `/markets` feed
   *  omits this and lets the card read it from `useMarketsSortStore` at click
   *  time, so a sort switch doesn't re-render every card. */
  activeSort?: string | undefined;
  /** LCP: above-the-fold cards eager-load their cover thumbnail. See MOR-566. */
  priorityImage?: boolean;
  /** Event-page presentation: replaces the consensus threshold with time until event resolution. */
  liveEvent?: MarketRowCardLiveEvent;
  /** Row observer ref from `<Items>` — wires viewport gating. */
  ref?: ForwardedRef<HTMLDivElement>;
}

export const MarketRowCard: ForwardRefExoticComponent<
  Omit<MarketRowCardProps, 'ref'> & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, Omit<MarketRowCardProps, 'ref'>>(
  function MarketRowCard(props, ref): React.ReactNode | null {
    const { pubkey, activeSort, priorityImage = false, liveEvent } = props;

    // Stable fields only — these change at most when a creator edits the
    // market, never on a trade, so the shell renders once and freezes. That
    // is what lets `MarketRowCardChrome` and the `barGrid` / `footer` element
    // identities stay constant across conviction / volume patches.
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const title = useGetMarketsField(pubkey, 'title');
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const coverImageUrl = useGetMarketsField(pubkey, 'coverImageUrl');
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const coverImageNsfw = useGetMarketsField(pubkey, 'coverImageNsfw');
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const location = useGetMarketsField(pubkey, 'location');
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const category = useGetMarketsField(pubkey, 'category');
    // `outcomeCount` is a scalar set at market creation — reading it for the
    // render guard does NOT churn on conviction patches the way `outcomes`
    // would, so the guard never re-renders the shell.
    // ALLOW-UNSCOPED: per-field leaf hook — the field name is the positional arg, already scoped to one field.
    const outcomeCount = useGetMarketsField(pubkey, 'outcomeCount');
    // Render guard — markets with fewer than 2 outcomes don't render. The
    // observer ref is still planted so `<Items>` viewport tracking works.
    if ((outcomeCount ?? 0) < 2) {
      return <div ref={ref} />;
    }

    return (
      <div ref={ref}>
        <FeedMarketThresholdProvider pubkey={pubkey}>
          <MarketRowCardChrome
            pubkey={pubkey}
            title={title ?? ''}
            coverImageUrl={coverImageUrl ?? ''}
            coverImageNsfw={coverImageNsfw ?? false}
            location={location ?? null}
            category={category ?? null}
            position={0}
            activeSort={activeSort}
            imageOverride={undefined}
            priorityImage={priorityImage}
            identity={<MarketFeedOriginMark pubkey={pubkey} />}
            beam={(panel: ReactNode): ReactNode => (
              <MarketFeedIdentityBeam pubkey={pubkey} className="h-full">
                {panel}
              </MarketFeedIdentityBeam>
            )}
            barGrid={<FeedBarGrid pubkey={pubkey} activeSort={activeSort} />}
            footer={
              <FeedFooter pubkey={pubkey} {...(liveEvent === undefined ? {} : { liveEvent })} />
            }
          />
        </FeedMarketThresholdProvider>
      </div>
    );
  },
);
